import api from './client';
import { supabase } from '@/lib/supabase';
import { isEmbedMode, getEmbedToken } from '@/lib/embedMode';
import { dlog } from '@/lib/debugLog';
/**
 * True when a credential exists to send (embed token or Supabase session).
 * Same sources as the api client's auth interceptor - no new auth path.
 */
async function hasSession() {
    try {
        if (isEmbedMode())
            return !!getEmbedToken();
        const { data: { session } } = await supabase.auth.getSession();
        return !!session?.access_token;
    }
    catch (error) {
        return false;
    }
}
/**
 * Get user preferences from the server
 * Returns null if not authenticated (silently falls back to localStorage)
 */
export async function getUserPreferences() {
    if (!(await hasSession())) {
        dlog('[Preferences] No signed-in session - skipping GET /preferences');
        return null;
    }
    try {
        const { data } = await api.get('/preferences');
        return data;
    }
    catch (error) {
        // Don't log 401 errors - user might not be logged in yet
        if (error.response?.status !== 401) {
            console.error('Failed to fetch user preferences:', error);
        }
        return null;
    }
}
/**
 * Save user preferences to the server
 * Returns true when the server answered 2xx, false when the request failed, null when skipped (no session).
 * Silently fails if not authenticated (uses localStorage only)
 */
export async function saveUserPreferences(preferences) {
    if (!(await hasSession())) {
        dlog('[Preferences] No signed-in session - skipping PUT /preferences');
        return null;
    }
    try {
        await api.put('/preferences', preferences);
        return true;
    }
    catch (error) {
        // Don't log 401 errors - user might not be logged in yet
        if (error.response?.status !== 401) {
            console.error('Failed to save user preferences:', error);
        }
        return false;
    }
}
